import { useEffect, useMemo, useState } from "react";
import { fetchBeliefs, fetchBelief } from "@/lib/api";
import {
  basicBlogAI,
  aeoBlogAI,
  liveVideoScriptAI,
  contentBatchAI,
  saveContentPiece,
} from "@/lib/ai";
import { PageHeader } from "@/components/PageHeader";
import { Tag } from "@/components/Tag";
import { Sparkles, Save, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "batch", label: "Weekly batch (Mon–Fri)" },
  { id: "blog_basic", label: "Basic blog" },
  { id: "blog_aeo", label: "AEO-ready blog" },
  { id: "live_video", label: "Live video script" },
];

export default function CreateContentPage() {
  const [beliefs, setBeliefs] = useState([]);
  const [selectedBeliefId, setSelectedBeliefId] = useState("");
  const [beliefDetail, setBeliefDetail] = useState(null);
  const [selectedStoryIds, setSelectedStoryIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState("batch");
  const [seoKeyword, setSeoKeyword] = useState("");
  const [cta, setCta] = useState("");
  const [triggerWord, setTriggerWord] = useState("PROTECT");
  const [freeOffer, setFreeOffer] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBeliefs().then(setBeliefs).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedBeliefId) {
      setBeliefDetail(null);
      setSelectedStoryIds(new Set());
      return;
    }
    fetchBelief(selectedBeliefId).then((d) => {
      setBeliefDetail(d);
      const approved = (d.stories || []).filter(
        (s) => s.review_status !== "pending_review",
      );
      // Default: select all approved stories
      setSelectedStoryIds(new Set(approved.map((s) => s.id)));
    });
    setResult(null);
  }, [selectedBeliefId]);

  const approvedStories = useMemo(() => {
    if (!beliefDetail) return [];
    return (beliefDetail.stories || []).filter(
      (s) => s.review_status !== "pending_review",
    );
  }, [beliefDetail]);

  const linkedStoriesForPrompt = useMemo(() => {
    return approvedStories
      .filter((s) => selectedStoryIds.has(s.id))
      .map((s) => ({
        story_type: s.story_type,
        story_text: s.story_text,
        source: s.source,
      }));
  }, [approvedStories, selectedStoryIds]);

  const toggleStory = (id) => {
    setSelectedStoryIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const generate = async () => {
    if (!beliefDetail) {
      toast.error("Pick a belief first.");
      return;
    }
    setLoading(true);
    setResult(null);
    const belief_text = beliefDetail.belief.belief_text;
    try {
      if (activeTab === "batch") {
        const res = await contentBatchAI({
          belief_text,
          linked_stories: linkedStoriesForPrompt,
        });
        setResult({ type: "batch", data: res });
      } else if (activeTab === "blog_basic") {
        const res = await basicBlogAI({
          belief_text,
          linked_stories: linkedStoriesForPrompt,
          seo_keyword: seoKeyword,
          cta,
        });
        setResult({ type: "markdown", data: res.markdown });
      } else if (activeTab === "blog_aeo") {
        const res = await aeoBlogAI({
          belief_text,
          linked_stories: linkedStoriesForPrompt,
          seo_keyword: seoKeyword,
          cta,
        });
        setResult({ type: "markdown", data: res.markdown });
      } else if (activeTab === "live_video") {
        const res = await liveVideoScriptAI({
          belief_text,
          linked_stories: linkedStoriesForPrompt,
          trigger_word: triggerWord,
          free_offer: freeOffer,
        });
        setResult({ type: "markdown", data: res.markdown });
      }
      toast.success("Generated. Review and save to your library.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const saveToLibrary = async () => {
    if (!result || !beliefDetail) return;
    setSaving(true);
    try {
      const meta = {
        belief_id: beliefDetail.belief.id,
        belief_text: beliefDetail.belief.belief_text,
        content_type: activeTab,
        title:
          activeTab === "batch"
            ? `Week of: ${beliefDetail.belief.belief_text.slice(0, 60)}`
            : activeTab === "live_video"
              ? "Live video script"
              : activeTab === "blog_aeo"
                ? "AEO blog"
                : "Basic blog",
        body: result.type === "batch" ? result.data : { markdown: result.data },
      };
      await saveContentPiece(meta);
      toast.success("Saved to Content Library.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="create-content-page">
      <PageHeader
        eyebrow="AI assist"
        title="Create content"
        subtitle="Pick a belief, pick the stories that support it, choose the format, and let Claude draft on-brand content for you to ship."
        testId="create-content-header"
      />

      <div className="bg-white border border-[#141B4D]/15 rounded-sm p-6 md:p-8 mb-8">
        <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
          1. Pick a belief
        </label>
        <select
          data-testid="content-belief-select"
          value={selectedBeliefId}
          onChange={(e) => setSelectedBeliefId(e.target.value)}
          className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100] mb-5"
        >
          <option value="">— Choose a belief —</option>
          {beliefs.map((b) => (
            <option key={b.id} value={b.id}>
              [{b.category || "?"}] {b.belief_text}
            </option>
          ))}
        </select>

        {beliefDetail && (
          <>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              2. Pick the linked stories to weave in ({selectedStoryIds.size}{" "}
              selected)
            </label>
            {approvedStories.length === 0 ? (
              <div className="border border-dashed border-[#141B4D]/25 rounded-sm p-4 text-xs text-[#141B4D]/60 mb-5">
                No approved stories yet. Go to the belief detail page and
                generate some — or pick a different belief.
              </div>
            ) : (
              <div className="space-y-2 mb-5 max-h-64 overflow-y-auto border border-[#141B4D]/15 rounded-sm p-3 bg-[#F2E9DB]/40">
                {approvedStories.map((s) => {
                  const active = selectedStoryIds.has(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleStory(s.id)}
                      data-testid={`content-story-toggle-${s.id}`}
                      className={`w-full text-left p-3 rounded-sm border transition-colors ${
                        active
                          ? "bg-[#FFD100]/30 border-[#FFD100]"
                          : "bg-white border-[#141B4D]/15 hover:bg-[#141B4D]/5"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <Tag>{s.story_type || "Story"}</Tag>
                        <p className="text-xs text-[#141B4D] leading-relaxed line-clamp-2 flex-1">
                          {s.story_text}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
          3. Pick the format
        </label>
        <div className="flex flex-wrap gap-2 mb-5" data-testid="content-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setActiveTab(t.id);
                setResult(null);
              }}
              data-testid={`content-tab-${t.id}`}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                activeTab === t.id
                  ? "bg-[#141B4D] text-[#F2E9DB] border-[#141B4D]"
                  : "bg-white text-[#141B4D] border-[#141B4D]/20 hover:bg-[#141B4D]/5"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {(activeTab === "blog_basic" || activeTab === "blog_aeo") && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                SEO / AEO keyword
              </label>
              <input
                data-testid="content-seo-keyword"
                value={seoKeyword}
                onChange={(e) => setSeoKeyword(e.target.value)}
                placeholder="e.g., LLC vs trademark"
                className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Call to action
              </label>
              <input
                data-testid="content-cta"
                value={cta}
                onChange={(e) => setCta(e.target.value)}
                placeholder="Book a free brand check call"
                className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
            </div>
          </div>
        )}

        {activeTab === "live_video" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Comment trigger word
              </label>
              <input
                data-testid="content-trigger-word"
                value={triggerWord}
                onChange={(e) => setTriggerWord(e.target.value)}
                placeholder="PROTECT"
                className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Free offer to send
              </label>
              <input
                data-testid="content-free-offer"
                value={freeOffer}
                onChange={(e) => setFreeOffer(e.target.value)}
                placeholder="Free Brand Check call"
                className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
            </div>
          </div>
        )}

        <button
          onClick={generate}
          disabled={loading || !selectedBeliefId}
          data-testid="content-generate-button"
          className="inline-flex items-center gap-2 bg-[#141B4D] text-[#F2E9DB] px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#141B4D]/90 disabled:opacity-60"
        >
          <Sparkles size={14} strokeWidth={2.5} />
          {loading ? "Writing…" : "Generate"}
        </button>
      </div>

      {result && (
        <div
          className="bg-white border border-[#141B4D]/15 rounded-sm p-6 md:p-8"
          data-testid="content-result"
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold text-[#141B4D]">Draft</h2>
            <button
              onClick={saveToLibrary}
              disabled={saving}
              data-testid="save-content-button"
              className="inline-flex items-center gap-2 bg-[#FFD100] text-[#141B4D] px-4 py-2 rounded-sm text-sm font-bold hover:bg-[#e6bd00] disabled:opacity-60"
            >
              <Save size={14} strokeWidth={2.5} />
              {saving ? "Saving…" : "Save to Library"}
            </button>
          </div>

          {result.type === "markdown" ? (
            <MarkdownResult markdown={result.data} />
          ) : (
            <BatchResult batch={result.data} />
          )}
        </div>
      )}
    </div>
  );
}

function CopyButton({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard.");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-sm border border-[#141B4D]/20 text-[#141B4D] hover:bg-[#141B4D]/5"
    >
      {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Copied" : "Copy"}
    </button>
  );
}

export function MarkdownResult({ markdown }) {
  return (
    <div>
      <div className="flex justify-end mb-3">
        <CopyButton text={markdown} testId="copy-markdown-button" />
      </div>
      <pre
        data-testid="markdown-output"
        className="bg-[#F2E9DB]/50 border border-[#141B4D]/15 rounded-sm p-5 text-sm text-[#141B4D] leading-relaxed whitespace-pre-wrap font-[Arial]"
      >
        {markdown}
      </pre>
    </div>
  );
}

function BatchRow({ day, label, children, copyText, testId }) {
  return (
    <div className="border-b border-[#141B4D]/15 py-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="text-[10px] uppercase tracking-[0.15em] text-[#141B4D]/50">
            {day}
          </span>
          <h3 className="text-base font-bold text-[#141B4D]">{label}</h3>
        </div>
        {copyText && <CopyButton text={copyText} testId={`${testId}-copy`} />}
      </div>
      <div className="text-sm text-[#141B4D] leading-relaxed whitespace-pre-wrap">
        {children}
      </div>
    </div>
  );
}

export function BatchResult({ batch }) {
  const video = batch.wednesday_video || {};
  return (
    <div className="space-y-1">
      <BatchRow
        day="Monday"
        label="Conversation starter"
        copyText={batch.monday_conversation_starter}
        testId="batch-monday"
      >
        {batch.monday_conversation_starter}
      </BatchRow>
      <BatchRow
        day="Tuesday"
        label="Barnum statement"
        copyText={batch.tuesday_barnum_statement}
        testId="batch-tuesday"
      >
        {batch.tuesday_barnum_statement}
      </BatchRow>
      <BatchRow
        day="Wednesday"
        label="Video"
        copyText={`${video.title}\n\n${video.hook}\n\n${(video.bullets || [])
          .map((b) => `• ${b}`)
          .join("\n")}\n\n${video.cta}`}
        testId="batch-wednesday"
      >
        <div className="space-y-2">
          <div>
            <span className="text-xs uppercase tracking-wider text-[#141B4D]/60">
              Title:{" "}
            </span>
            <span className="font-medium">{video.title}</span>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-[#141B4D]/60">
              Hook:{" "}
            </span>
            {video.hook}
          </div>
          <ul className="list-disc pl-5 space-y-1">
            {(video.bullets || []).map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
          <div>
            <span className="text-xs uppercase tracking-wider text-[#141B4D]/60">
              CTA:{" "}
            </span>
            {video.cta}
          </div>
        </div>
      </BatchRow>
      <BatchRow
        day="Thursday"
        label="Hand-raiser"
        copyText={batch.thursday_hand_raiser}
        testId="batch-thursday"
      >
        {batch.thursday_hand_raiser}
      </BatchRow>
      <BatchRow
        day="Friday"
        label="Snarky + meme"
        copyText={`${batch.friday_snarky}\n\n${batch.friday_meme_idea}`}
        testId="batch-friday"
      >
        <div className="space-y-3">
          <div className="font-medium">{batch.friday_snarky}</div>
          <div className="text-[#141B4D]/80 italic">{batch.friday_meme_idea}</div>
        </div>
      </BatchRow>
    </div>
  );
}
